import { create } from 'zustand';
import { resolveEffectiveUnitPrice } from '@shared/priceLists/resolveEffectiveUnitPrice';
import {
  getPriceListItemsLocal,
  listActivePriceListsLocal,
  type PriceList,
  type PriceListItem,
} from '../../priceLists/public-api';

type PriceListCache = {
  listsById: Map<string, PriceList>;
  itemsByListId: Map<string, PriceListItem[]>;
};

type PosPriceListState = {
  activePriceListId: string | null;
  cache: PriceListCache;
  setActivePriceListId: (id: string | null) => void;
  refreshCache: () => Promise<void>;
  resolveProductUnitPrice: (args: {
    productId: string;
    variantId?: string | null;
    basePrice: number;
    quantity?: number;
  }) => number;
};

const emptyCache = (): PriceListCache => ({
  listsById: new Map(),
  itemsByListId: new Map(),
});

export const usePosPriceListStore = create<PosPriceListState>((set, get) => ({
  activePriceListId: null,
  cache: emptyCache(),
  setActivePriceListId: (id) => set({ activePriceListId: id }),
  refreshCache: async () => {
    const lists = await listActivePriceListsLocal();
    const listsById = new Map(lists.map((l) => [l.id, l]));
    const itemsByListId = new Map<string, PriceListItem[]>();
    for (const list of lists) {
      itemsByListId.set(list.id, await getPriceListItemsLocal(list.id));
    }
    set({ cache: { listsById, itemsByListId } });
    if (!get().activePriceListId) {
      const defaultList = lists.find((l) => l.isDefault) ?? lists[0];
      if (defaultList) set({ activePriceListId: defaultList.id });
    }
  },
  resolveProductUnitPrice: ({ productId, variantId, basePrice, quantity }) => {
    const { activePriceListId, cache } = get();
    let listId = activePriceListId;
    if (!listId) {
      const defaultList = [...cache.listsById.values()].find((l) => l.isDefault);
      listId = defaultList?.id ?? null;
    }
    if (!listId) return basePrice;
    const list = cache.listsById.get(listId);
    if (!list || !list.isActive) return basePrice;
    const items = cache.itemsByListId.get(listId) ?? [];
    return resolveEffectiveUnitPrice({
      basePrice,
      productId,
      variantId,
      quantity,
      priceList: { id: list.id, pricingMode: list.pricingMode, isActive: list.isActive },
      items: items.map((item) => ({
        productId: item.productId,
        variantId: item.variantId,
        price: item.price,
        percentOff: item.percentOff,
        minQty: item.minQty,
      })),
    });
  },
}));
